/**
 * HedgeFundAI — pure behaviour tree for "The Hedge Fund" Stage 8 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Three rotating stances driven by HP thresholds:
 *
 *   Quant  (HP > 66%)  — ranged briefcase-drone volleys at any distance
 *   Raider (33–66% HP) — aggressive rushdown combos: rush_combo + haymaker
 *   Broker (HP ≤ 33%)  — defensive shield_bash + one-time corporate-security summon
 *
 * Stance transitions: Quant→Raider at 66%, Raider→Broker at 33%.
 * The attack cycle resets to 0 whenever the stance changes.
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Raider rush_combo requires close quarters. */
export const RUSH_RANGE = 110;
/** Raider haymaker swings from medium distance. */
export const HAYMAKER_RANGE = 160;
/** Broker shield_bash is a short counter. */
export const SHIELD_BASH_RANGE = 80;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Quant winds up a drone launch. */
export const DRONE_WINDUP_FRAMES = 20;
/** Raider rush combo has a short telegraph. */
export const RUSH_WINDUP_FRAMES = 14;
/** Raider haymaker has a longer wind-up arc. */
export const HAYMAKER_WINDUP_FRAMES = 22;
/** Broker shield_bash is a reactive counter — quick telegraph. */
export const SHIELD_BASH_WINDUP_FRAMES = 12;
/** Retreat frames after a melee attack. */
export const BACKSTEP_FRAMES = 26;
/** Frames the Hedge Fund shouts into a phone while summoning corporate security. */
export const SUMMON_FRAMES = 32;
/** Speed during backstep. */
export const HEDGE_FUND_BACKSTEP_SPEED = 1.2;
/** Base inter-attack cooldown at aggressionScale = 0. */
const BASE_COOLDOWN = 72;

// ── Stance thresholds ─────────────────────────────────────────────────────────
/** HP fraction below which stance shifts from Quant to Raider. */
export const RAIDER_THRESHOLD = 0.66;
/** HP fraction below which stance shifts from Raider to Broker. */
export const BROKER_THRESHOLD = 0.33;

// ── Types ─────────────────────────────────────────────────────────────────────
export type HedgeFundStance = 'quant' | 'raider' | 'broker';

export type HedgeFundAttack = 'briefcase_drone' | 'rush_combo' | 'haymaker' | 'shield_bash';

export type HedgeFundNode =
  | 'scan'
  | 'approach'
  | 'drone_windup'
  | 'rush_windup'
  | 'haymaker_windup'
  | 'shield_bash_windup'
  | 'summoning'
  | 'backstep';

export interface HedgeFundAIState {
  node: HedgeFundNode;
  stance: HedgeFundStance;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  summonFrames: number;
  /** Round-robin index within the current stance's attack list. */
  attackCycle: number;
  /** True once corporate-security has been summoned (Broker entry, one-time). */
  securitySummoned: boolean;
}

export interface HedgeFundAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface HedgeFundAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: HedgeFundAttack | null;
  /** True on the single frame the adapter should spawn a corporate-security minion. */
  wantsSummonSecurity: boolean;
  nextState: HedgeFundAIState;
}

export function initialHedgeFundState(): HedgeFundAIState {
  return {
    node: 'scan',
    stance: 'quant',
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    securitySummoned: false,
  };
}

/**
 * Resolve stance from HP fraction.
 */
export function stanceFor(hpFraction: number): HedgeFundStance {
  if (hpFraction <= BROKER_THRESHOLD) return 'broker';
  if (hpFraction <= RAIDER_THRESHOLD) return 'raider';
  return 'quant';
}

/**
 * Advance the Hedge Fund behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns
 * hitboxes / security summons based on the output.
 */
export function tickHedgeFundAI(
  input: HedgeFundAIInput,
  state: HedgeFundAIState,
): HedgeFundAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialHedgeFundState());
  }

  // ── Stance resolution (re-derived every frame from live HP) ───────────────
  const newStance = stanceFor(input.hpFraction);
  const stanceChanged = newStance !== state.stance;

  // When stance changes, reset attack cycle so each persona starts fresh.
  const attackCycleBase = stanceChanged ? 0 : state.attackCycle;

  const { securitySummoned } = state;

  // ── Broker entry: one-time corporate-security summon ──────────────────────
  if (newStance === 'broker' && !securitySummoned && state.node !== 'summoning') {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      stance: newStance,
      securitySummoned: true,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: SUMMON_FRAMES,
      attackCycle: 0,
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    // If a hit interrupts the summon wind-up, re-arm the dedup flag: the spawn
    // signal only fires at wind-up completion, so without this the corporate
    // security detail is silently never summoned. The entry gate re-fires it on
    // a later clean frame — still exactly once.
    const interruptedSummon = state.node === 'summoning';
    return noOp(facing, {
      ...state,
      stance: newStance,
      securitySummoned: interruptedSummon ? false : securitySummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      summonFrames: 0,
      attackCycle: attackCycleBase,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, {
        ...state,
        stance: newStance,
        securitySummoned,
        cooldown,
        summonFrames,
      });
    }
    // Animation complete — fire the one-time spawn signal
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonSecurity: true,
      nextState: {
        ...state,
        node: 'scan',
        stance: newStance,
        securitySummoned,
        cooldown: cooldownFor(input.aggressionScale),
        summonFrames: 0,
        attackCycle: 0,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * HEDGE_FUND_BACKSTEP_SPEED,
        moveY: away.y * HEDGE_FUND_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonSecurity: false,
        nextState: {
          ...state,
          stance: newStance,
          securitySummoned,
          cooldown,
          backstepFrames,
          attackCycle: attackCycleBase,
        },
      };
    }
    return noOp(facing, {
      ...state,
      stance: newStance,
      securitySummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
      attackCycle: attackCycleBase,
    });
  }

  // ── DRONE WINDUP (Quant) ───────────────────────────────────────────────────
  if (state.node === 'drone_windup') {
    if (windup > 0)
      return noOp(facing, { ...state, stance: newStance, securitySummoned, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'briefcase_drone',
      wantsSummonSecurity: false,
      nextState: {
        ...state,
        stance: newStance,
        securitySummoned,
        node: 'scan',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        attackCycle: nextCycle(attackCycleBase, 1),
      },
    };
  }

  // ── RUSH WINDUP (Raider cycle 0) ──────────────────────────────────────────
  if (state.node === 'rush_windup') {
    if (windup > 0)
      return noOp(facing, { ...state, stance: newStance, securitySummoned, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'rush_combo',
      wantsSummonSecurity: false,
      nextState: {
        ...state,
        stance: newStance,
        securitySummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(attackCycleBase, 2),
      },
    };
  }

  // ── HAYMAKER WINDUP (Raider cycle 1) ──────────────────────────────────────
  if (state.node === 'haymaker_windup') {
    if (windup > 0)
      return noOp(facing, { ...state, stance: newStance, securitySummoned, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'haymaker',
      wantsSummonSecurity: false,
      nextState: {
        ...state,
        stance: newStance,
        securitySummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(attackCycleBase, 2),
      },
    };
  }

  // ── SHIELD BASH WINDUP (Broker) ───────────────────────────────────────────
  if (state.node === 'shield_bash_windup') {
    if (windup > 0)
      return noOp(facing, { ...state, stance: newStance, securitySummoned, cooldown, windup });
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'shield_bash',
      wantsSummonSecurity: false,
      nextState: {
        ...state,
        stance: newStance,
        securitySummoned,
        node: 'scan',
        cooldown: cooldownFor(input.aggressionScale),
        windup: 0,
        attackCycle: nextCycle(attackCycleBase, 1),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ─────────────────────────────────
  const { dist } = toTarget(input);
  const attackCycle = attackCycleBase;

  if (cooldown === 0 && input.canAttack) {
    // ── Quant: drone at any range ─────────────────────────────────────────
    if (newStance === 'quant') {
      return noOp(facing, {
        ...state,
        stance: newStance,
        securitySummoned,
        cooldown,
        node: 'drone_windup',
        windup: DRONE_WINDUP_FRAMES,
        attackCycle,
      });
    }

    // ── Raider: rush_combo (0) or haymaker (1) ────────────────────────────
    if (newStance === 'raider') {
      if (attackCycle === 0 && dist <= RUSH_RANGE) {
        return noOp(facing, {
          ...state,
          stance: newStance,
          securitySummoned,
          cooldown,
          node: 'rush_windup',
          windup: RUSH_WINDUP_FRAMES,
          attackCycle,
        });
      }
      if (attackCycle === 1 && dist <= HAYMAKER_RANGE) {
        return noOp(facing, {
          ...state,
          stance: newStance,
          securitySummoned,
          cooldown,
          node: 'haymaker_windup',
          windup: HAYMAKER_WINDUP_FRAMES,
          attackCycle,
        });
      }
    }

    // ── Broker: shield_bash at close range ───────────────────────────────
    if (newStance === 'broker' && dist <= SHIELD_BASH_RANGE) {
      return noOp(facing, {
        ...state,
        stance: newStance,
        securitySummoned,
        cooldown,
        node: 'shield_bash_windup',
        windup: SHIELD_BASH_WINDUP_FRAMES,
        attackCycle,
      });
    }
  }

  // ── Approach / orbit ──────────────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: HedgeFundNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonSecurity: false,
      nextState: {
        ...state,
        stance: newStance,
        securitySummoned,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        summonFrames: 0,
        attackCycle,
      },
    };
  }

  return noOp(facing, {
    ...state,
    stance: newStance,
    securitySummoned,
    cooldown,
    node: 'scan',
    attackCycle,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: HedgeFundAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: HedgeFundAIState): HedgeFundAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonSecurity: false,
    nextState,
  };
}

/** Advance cycle mod `mod`. */
function nextCycle(c: number, mod: number): number {
  return (c + 1) % mod;
}
